import React, { useMemo } from 'react';
import { Status } from '@/components';
import { RentalDetail } from '@/types/rental';
import { buildActivityTimeline } from '../rentalDisplay';

interface RentalTimelineProps {
  detail: RentalDetail;
}

export const RentalTimeline: React.FC<RentalTimelineProps> = ({ detail }) => {
  // The API's own status history, newest first. Rows are raw transitions, so
  // the wording is applied here rather than coming from the server.
  const entries = useMemo(
    // driverName so a `driver:<id>` actor reads as the mitra's name rather
    // than an opaque id.
    () =>
      buildActivityTimeline(
        detail.timeline,
        detail.driver?.name,
        detail.reassignments
      ),
    [detail.timeline, detail.driver?.name, detail.reassignments]
  );

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-5 py-4">
        <h2 className="text-[18px] font-medium text-gray-900">
          Timeline Status
        </h2>
      </div>

      <div className="bg-gray-50 px-5 py-2.5 border-y border-gray-100">
        <span className="text-[13px] text-gray-500">Detail</span>
      </div>

      <div className="px-5 py-5">
        {entries.length === 0 ? (
          <p className="py-8 text-center text-[14px] text-gray-500">
            Belum ada aktivitas pada sewa ini.
          </p>
        ) : (
          <ol className="relative">
            {entries.map((entry, index) => {
              const isLatest = index === 0;
              const isLast = index === entries.length - 1;

              return (
                <li key={entry.key} className="relative flex gap-3 pb-6">
                  {/* Connector, stopping at the last entry. */}
                  {!isLast && (
                    <span
                      aria-hidden="true"
                      className="absolute left-[15px] top-8 bottom-0 w-px bg-gray-200"
                    />
                  )}

                  <span
                    className={`relative z-[1] flex h-8 w-8 shrink-0 items-center justify-center rounded-full border ${
                      isLatest
                        ? 'border-primary bg-primary text-white'
                        : 'border-gray-200 bg-white text-gray-400'
                    }`}
                  >
                    <i className="ri-check-line text-[14px]"></i>
                  </span>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <span
                        className={`text-[15px] ${
                          isLatest
                            ? 'font-medium text-gray-900'
                            : 'text-gray-500'
                        }`}
                      >
                        {entry.date}
                      </span>
                      {entry.time && (
                        <span className="text-[13px] text-gray-400 shrink-0">
                          {entry.time}
                        </span>
                      )}
                    </div>

                    <p
                      className={`mt-0.5 text-[14px] leading-[20px] ${
                        isLatest ? 'text-gray-700' : 'text-gray-500'
                      }`}
                    >
                      {entry.description}
                    </p>

                    <div className="mt-1.5">
                      <Status
                        value={entry.chip}
                        type="timelineEvent"
                        size="medium"
                      />
                    </div>

                    {entry.actor && (
                      <div className="mt-1.5 flex items-center gap-1.5 text-[12px] text-gray-400">
                        <i
                          aria-hidden="true"
                          className="ri-user-line text-[13px]"
                        ></i>
                        <span className="min-w-0 break-all">{entry.actor}</span>
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </div>
  );
};

export default RentalTimeline;
